// scopeManager.ts

import { db, firebaseImport } from '../../config/main_config'; // Adjust the path as needed
import { createToast } from './createToast.util';
import { closeModal } from './closeModal.util';
import { waitForCompanyID } from './waitForCompanyID.util';
import { openModal } from './openModal.util';
import Tableordion from './tableordion.util';
import { sanitise } from './sanitise.util';

/**
 * Interface representing a Scope Area and its Description.
 */
interface ScopeItem {
    scopeArea: string;
    description: string;
}

/**
 * Interface representing a history record for a Scope Area description.
 */
interface ScopeHistory {
    timestamp: string;
    description: string;
}

/**
 * Converts camelCase strings to space-separated words with each word capitalized.
 * 
 * @param str - The camelCase string to convert.
 * @returns The converted string with spaces and capitalized words.
 */
function camelCaseToSpaces(str: string): string {
    let spaced = str.replace(/([a-z])([A-Z])/g, '$1 $2');

    spaced = spaced.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');

    const capitalized = spaced.replace(/\b\w/g, char => char.toUpperCase());

    return capitalized;
}

/**
 * Converts space-separated strings to camelCase.
 * 
 * @param str - The space-separated string to convert.
 * @returns The camelCase converted string.
 */
function spacesToCamelCase(str: string): string {
    // Step 1: Split the string into words using space as the delimiter
    const words = str.split(' ').filter(word => word.length > 0); // Handle multiple spaces

    // Step 2: Process each word
    const camelCased = words.map((word, index) => {
        // Convert the word to lowercase to ensure consistency
        word = word.toLowerCase();

        if (index === 0) {
            // First word: all lowercase
            return word;
        } else {
            // Subsequent words: capitalize the first letter
            return word.charAt(0).toUpperCase() + word.slice(1);
        }
    });

    // Step 3: Join the words without spaces to form camelCase
    return camelCased.join('');
}

/**
 * Fetches scope data from Firestore.
 * 
 * @returns A promise that resolves to an array of ScopeItems.
 */
export async function fetchScopeData(): Promise<ScopeItem[]> {
    const companyID = await waitForCompanyID(); // Dynamic retrieval of companyID
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsScope`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        if (doc.exists) {
            const data = doc.data()?.content?.field6?.text;
            if (data && typeof data === 'object') {
                // Assuming data is an object where keys are Scope Areas and values are Descriptions
                const scopeItems: ScopeItem[] = Object.entries(data).map(([scopeArea, description]) => ({
                    scopeArea,
                    description: description as string,
                }));
                return scopeItems;
            } else {
                console.warn('No valid data found in the specified document.');
                return [];
            }
        } else {
            console.warn('No such document!');
            return [];
        }
    } catch (error) {
        console.error('Error fetching scope data:', error);
        createToast({
            message: 'Failed to load scope data.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Sets up a real-time listener to Firestore to listen for changes in scope data.
 * 
 * @param onDataChange - Callback function to handle the updated scope items.
 * @returns A function to unsubscribe the listener.
 */
export function listenToScopeData(onDataChange: (scopeItems: ScopeItem[]) => void): () => void {
    let unsubscribe: () => void;

    waitForCompanyID().then((companyID) => {
        const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsScope`;
        const docRef: firebaseImport.firestore.DocumentReference = db.doc(docPath);

        unsubscribe = docRef.onSnapshot((doc) => {
            if (doc.exists) {
                const data = doc.data()?.content?.field6?.text;
                if (data && typeof data === 'object') {
                    const scopeItems: ScopeItem[] = Object.entries(data).map(([scopeArea, description]) => ({
                        scopeArea,
                        description: description as string,
                    }));
                    onDataChange(scopeItems);
                } else {
                    console.warn('No valid data found in the specified document.');
                    onDataChange([]);
                }
            } else {
                console.warn('No such document!');
                onDataChange([]);
            }
        }, (error) => {
            console.error('Error listening to scope data:', error);
            createToast({
                message: 'Failed to listen to scope data.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
        });
    }).catch((error) => {
        console.error('Error waiting for company ID:', error);
        createToast({
            message: 'Failed to initialize scope data listener.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    });

    // Return the unsubscribe function to allow cleanup
    return () => {
        if (unsubscribe) {
            unsubscribe();
            console.log('Scope data listener unsubscribed.');
        }
    };
}

/**
 * Initialises the Scope Manager by setting up real-time listeners.
 */
export async function initialiseScopeManager(): Promise<() => void> {
    // Set up the real-time listener
    const unsubscribe = listenToScopeData(async (scopeItems) => {
        // Render the table with the updated scope items
        await renderTableordion(scopeItems);
    });

    // Return the unsubscribe function to allow caller to clean up
    return unsubscribe;
}

/**
 * Renders the scope data into a tableordion.
 * 
 * @param scopeItems - Array of ScopeItem objects.
 */
export async function renderTableordion(scopeItems: ScopeItem[]): Promise<void> {
    const tableContainer = document.querySelector('.tableordion-body');

    if (!tableContainer) {
        console.error('Table accordion container not found.');
        return;
    }

    let html = '';

    // Sort the scope items by Scope Area
    scopeItems.sort((a, b) => a.scopeArea.localeCompare(b.scopeArea, undefined, { numeric: true, sensitivity: 'base' }));

    // Table rows
    scopeItems.forEach((item, index) => {
        html += `
            <tr data-index="${index}" class="tableordion-row" style="cursor: default;">
                <td>${camelCaseToSpaces(item.scopeArea)}</td>
                <td>${sanitise(item.description)}</td>
                <td>
                    <button class="action-btn edit-description-btn" data-scope="${encodeURIComponent(item.scopeArea)}">
                        <i class="fa-duotone fa-solid fa-pencil edit-icon" alt="Edit"></i>
                    </button>
                </td>
            </tr>
        `;
    });

    tableContainer.innerHTML = html;
    let accordions = new Tableordion('.tableordion-row');

    // Attach event listeners to edit buttons
    attachEditButtonsListeners(scopeItems);
}

/**
 * Attaches event listeners to all edit buttons in the tableordion.
 * 
 * @param scopeItems - Array of ScopeItem objects.
 */
function attachEditButtonsListeners(scopeItems: ScopeItem[]): void {
    const editButtons = document.querySelectorAll('.edit-description-btn');

    editButtons.forEach(button => {
        button.addEventListener('click', async (event) => {
            const target = event.currentTarget as HTMLElement;
            const encodedScopeArea = target.getAttribute('data-scope');

            if (!encodedScopeArea) {
                console.error('Scope Area not found on the edit button.');
                return;
            }

            const scopeArea = decodeURIComponent(encodedScopeArea);
            const description = getDescriptionByScopeArea(scopeArea, scopeItems);

            if (description === null) {
                console.error('Description not found for the given Scope Area.');
                return;
            }

            openEditDescriptionModal(scopeArea, description);
        });
    });
}

/**
 * Retrieves the description for a given Scope Area.
 * 
 * @param scopeArea - The Scope Area name.
 * @param scopeItems - Array of ScopeItem objects.
 * @returns The Description string or null if not found.
 */
function getDescriptionByScopeArea(scopeArea: string, scopeItems: ScopeItem[]): string | null {
    const foundItem = scopeItems.find(item => item.scopeArea === scopeArea);
    return foundItem ? foundItem.description : null;
}

/**
 * Opens the edit description modal and populates it with current data.
 * 
 * @param scopeArea - The Scope Area being edited.
 * @param currentDescription - The current Description.
 */
function openEditDescriptionModal(scopeArea: string, currentDescription: string): void {
    const modal = document.querySelector('[data-managedid="EditDescriptionModal"]') as HTMLElement;
    if (!modal) {
        console.error('Edit Description Modal not found.');
        return;
    }

    // Set Scope Area (read-only)
    const scopeAreaElement = document.getElementById('scopeArea_edit') as HTMLInputElement;
    if (scopeAreaElement) {
        scopeAreaElement.value = camelCaseToSpaces(scopeArea);
    }

    // Set current Description
    const descriptionElement = document.getElementById('description_edit') as HTMLTextAreaElement;
    if (descriptionElement) {
        descriptionElement.value = currentDescription;
    }

    // Display the modal using your existing modal management function
    openModal('[data-managedid="EditDescriptionModal"]');
}

/**
 * Formats a Date object to 'yyyy-MM-dd' string.
 * 
 * @param date - The Date object to format.
 * @returns A string representing the date in 'yyyy-MM-dd' format.
 */
function formatDateToInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Handles the form submission for editing descriptions.
 * 
 * @param event - The event triggered by the save button.
 */
export async function saveEditedDescription(event: Event): Promise<void> {
    event.preventDefault();

    // Retrieve the modal element
    const editModal = document.querySelector('[data-managedid="EditDescriptionModal"]') as HTMLElement;
    if (!editModal) {
        console.error('Edit Description Modal not found.');
        return;
    }

    // Retrieve the Scope Area and new Description
    const scopeArea = (document.getElementById('scopeArea_edit') as HTMLInputElement).value.trim();
    const newDescription = (document.getElementById('description_edit') as HTMLTextAreaElement).value.trim();

    // Validate inputs
    if (!newDescription) {
        createToast({
            message: 'Description cannot be empty.',
            customStyles: {
                backgroundColor: '#ffcc00',
                color: '#000000'
            }
        });
        return;
    }

    // Update the description in Firestore
    await updateScopeDescription(scopeArea, newDescription);

    // Close the modal
    closeModal(editModal);
}

/**
 * Updates the description of a specific Scope Area in Firestore.
 * 
 * @param scopeArea - The Scope Area to update.
 * @param newDescription - The new Description text.
 */
export async function updateScopeDescription(scopeArea: string, newDescription: string): Promise<void> {
    const companyID = await waitForCompanyID(); // Dynamic retrieval of companyID
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsScope`;
    const docRef = db.doc(docPath);

    try {
        const camelCaseScopeArea = spacesToCamelCase(scopeArea); // Ensure correct scopeArea format

        // Update the specific Scope Area's description within the 'text' field
        await docRef.update({
            [`content.field6.text.${camelCaseScopeArea}`]: newDescription
        });
        console.log(`Description for Scope Area "${scopeArea}" updated successfully.`);
        createToast({
            message: `Description for "${scopeArea}" updated successfully.`,
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

        // No need to manually refresh the table as onSnapshot will handle it
    } catch (error) {
        console.error('Error updating description:', error);
        createToast({
            message: 'Failed to update description.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}